import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { uploadAvatar, deleteAvatar, updateProfile, getPaymentTiers, cancelSubscription, deleteAccount, getMyRank } from '../services/api';
import { User, Settings, Crown, Calendar, Camera, Trash2, Loader2, Upload, Lock, Bell } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import NotificationSettings from '../components/NotificationSettings';
import { RankBadge } from '../components/RankBadge';
import { Link } from 'react-router-dom';

const ProfilePage = () => {
  const { currentUser, updateUser, isOwner, isModerator, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [tiers, setTiers] = useState({});
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    displayName: currentUser?.displayName || '',
    bio: currentUser?.bio || '',
    email: currentUser?.email || ''
  });

  const loadTiers = useCallback(async () => {
    try {
      const response = await getPaymentTiers();
      setTiers(response.tiers || {});
    } catch (error) {
    }
  }, []);

  const [myRank, setMyRank] = useState(null);
  const loadRank = useCallback(async () => {
    try {
      const data = await getMyRank();
      setMyRank(data);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    loadTiers();
    loadRank();
  }, [loadTiers, loadRank]);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You\'ll keep access until the end of your billing period.')) return;
    setCancelingSubscription(true);
    try {
      await cancelSubscription();
      updateUser({ subscriptionStatus: 'canceling' });
      toast({ title: 'Subscription Canceling', description: 'Your subscription will end at the end of the current billing period.' });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to cancel subscription', variant: 'destructive' });
    } finally {
      setCancelingSubscription(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await deleteAccount();
      toast({ title: 'Account Deleted', description: 'Your account has been permanently deleted.' });
      logout();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete account', variant: 'destructive' });
    } finally {
      setDeletingAccount(false);
      setShowDeleteConfirm(false);
    }
  };


  const currentTier = tiers[currentUser?.subscriptionTier] || { name: 'Free', price: 0, benefits: [] };

  useEffect(() => {
    if (window.location.hash !== '#billing') return undefined;
    const t = setTimeout(() => document.getElementById('billing')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 400);
    return () => clearTimeout(t);
  }, []);

  
  // Check if user can upload avatar (owner, moderator, or paid tier)
  const canUploadAvatar = isOwner || isModerator || ['bronze', 'silver', 'gold'].includes(currentUser?.subscriptionTier);

  const handleAvatarClick = () => {
    if (canUploadAvatar) {
      fileInputRef.current?.click();
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JPEG, PNG, GIF, or WebP image',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Avatar image must be less than 2MB',
        variant: 'destructive'
      });
      return;
    }

    try {
      setUploadingAvatar(true);
      const result = await uploadAvatar(file);
      
      // Update local user state
      updateUser({ avatarUrl: result.avatarUrl });
      
      toast({
        title: 'Avatar updated',
        description: 'Your profile picture has been updated'
      });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error.response?.data?.detail || 'Failed to upload avatar',
        variant: 'destructive'
      });
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      setUploadingAvatar(true);
      const result = await deleteAvatar();
      
      // Update local user state
      updateUser({ avatarUrl: result.avatarUrl });
      
      toast({
        title: 'Avatar removed',
        description: 'Your profile picture has been reset to default'
      });
    } catch (error) {
      toast({
        title: 'Failed to remove avatar',
        description: error.response?.data?.detail || 'An error occurred',
        variant: 'destructive'
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      const updatedUser = await updateProfile({
        displayName: formData.displayName,
        bio: formData.bio
      });
      
      updateUser(updatedUser);
      setIsEditing(false);
      
      toast({
        title: 'Profile updated',
        description: 'Your profile has been saved'
      });
    } catch (error) {
      toast({
        title: 'Update failed',
        description: error.response?.data?.detail || 'Failed to update profile',
        variant: 'destructive'
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getRoleBadge = () => {
    if (isOwner) return <Badge className="bg-red-700 font-headline tracking-wider">LEAD DETECTIVE</Badge>;
    if (isModerator) return <Badge className="bg-amber-700 font-headline tracking-wider">SENIOR AGENT</Badge>;
    return <Badge className="bg-gray-700 font-headline tracking-wider">INVESTIGATOR</Badge>;
  };

  const getTierColor = (tierName) => {
    switch (tierName?.toLowerCase()) {
      case 'bronze': return '#CD7F32';
      case 'silver': return '#C0C0C0';
      case 'gold': return '#FFD700';
      default: return '#6B7280';
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      className="min-h-screen bg-black relative py-8" 
      data-testid="profile-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Texture overlay */}
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
      
      <div className="container mx-auto px-4 max-w-4xl relative z-10">
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <h1 className="text-3xl font-headline text-amber-500 tracking-wider">CASE FILE: {currentUser.displayName?.toUpperCase()}</h1>
          <p className="text-gray-400 font-typewriter">Manage your investigator profile and clearance</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList className="bg-gray-900 border-2 border-amber-900/30">
              <TabsTrigger value="profile" className="data-[state=active]:bg-amber-900/30 data-[state=active]:text-amber-500 font-headline tracking-wider transition-all">
                <User className="w-4 h-4 mr-2" />
                PROFILE
              </TabsTrigger>
              <TabsTrigger value="notifications" className="data-[state=active]:bg-amber-900/30 data-[state=active]:text-amber-500 font-headline tracking-wider transition-all">
                <Bell className="w-4 h-4 mr-2" />
                ALERTS
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-amber-900/30 data-[state=active]:text-amber-500 font-headline tracking-wider transition-all">
                <Settings className="w-4 h-4 mr-2" />
                SETTINGS
              </TabsTrigger>
            </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            {myRank?.isPaid && myRank.currentRank && (
              <Card className="bg-gradient-to-br from-gray-900 to-black border-2 border-amber-900/40" data-testid="profile-rank-card">
                <CardContent className="p-5 flex items-center gap-5">
                  <RankBadge rank={myRank.currentRank} size="lg" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-headline text-xl tracking-widest text-amber-300">{myRank.currentRank.rankName.toUpperCase()}</h3>
                      {myRank.currentRank.isOG && (
                        <Badge className="bg-yellow-700/40 text-yellow-300 border border-yellow-500/50 font-headline tracking-widest text-[10px]">OG</Badge>
                      )}
                    </div>
                    <p className="text-gray-400 font-typewriter text-sm mt-1">
                      {myRank.tenureDays} days of service
                      {myRank.nextRank && <> · <span className="text-amber-400">{myRank.daysToNext} days</span> to {myRank.nextRank.rankName}</>}
                    </p>
                    {myRank.nextRank && (
                      <div className="h-2 bg-gray-800 border border-amber-900/40 rounded-sm overflow-hidden mt-2 max-w-xs">
                        <div className="h-full bg-gradient-to-r from-amber-700 to-red-500" style={{ width: `${myRank.progressPct}%` }} />
                      </div>
                    )}
                  </div>
                  <Link to="/ranks">
                    <Button variant="outline" size="sm" className="border-amber-700 text-amber-400 hover:bg-amber-900/30 font-headline tracking-wider" data-testid="profile-view-ranks-btn">
                      View Ranks
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">INVESTIGATOR PROFILE</CardTitle>
                <CardDescription className="font-typewriter text-gray-400">Your public identity in the investigation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Avatar Section */}
                <div className="flex items-start gap-6">
                  <div className="relative group">
                    <Avatar className="h-24 w-24 border-4 border-amber-700/50">
                      <AvatarImage src={currentUser.avatarUrl} />
                      <AvatarFallback className="text-2xl bg-amber-900/50 text-amber-500 font-headline">
                        {currentUser.displayName?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    
                    {canUploadAvatar && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        {uploadingAvatar ? (
                          <Loader2 className="w-6 h-6 text-white animate-spin" />
                        ) : (
                          <button
                            onClick={handleAvatarClick}
                            className="p-2 bg-amber-600 rounded-full hover:bg-amber-700 transition-colors"
                          >
                            <Camera className="w-5 h-5 text-black" />
                          </button>
                        )}
                      </div>
                    )}
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      onChange={handleAvatarChange}
                      data-testid="avatar-input"
                    />
                  </div>
                  
                  <div className="space-y-3 flex-1">
                    <div>
                      <h3 className="text-xl font-headline text-gray-100 tracking-wide">{currentUser.displayName}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {getRoleBadge()}
                        <Badge style={{ backgroundColor: `${getTierColor(currentTier.name)}20`, color: getTierColor(currentTier.name) }} className="font-headline tracking-wider">
                          {currentTier.name?.toUpperCase()} CLEARANCE
                        </Badge>
                      </div>
                    </div>
                    
                    {canUploadAvatar ? (
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleAvatarClick}
                          disabled={uploadingAvatar}
                          className="border-amber-700 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider"
                          data-testid="change-avatar-btn"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          CHANGE PHOTO
                        </Button>
                        {currentUser.avatarUrl && !currentUser.avatarUrl.includes('dicebear') && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={handleRemoveAvatar}
                            disabled={uploadingAvatar}
                            className="text-red-500 hover:bg-red-900/20"
                            data-testid="remove-avatar-btn"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-gray-500 font-typewriter">
                        <Lock className="w-4 h-4" />
                        <span>Upgrade to a paid tier to upload a custom avatar</span>
                      </div>
                    )}
                  </div>
                </div>

                <Separator className="bg-amber-900/30" />

                {/* Editable Fields */}
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="displayName" className="text-amber-500 font-headline tracking-wider">DISPLAY NAME</Label>
                      <Input
                        id="displayName"
                        value={formData.displayName}
                        onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                        className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                        data-testid="display-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bio" className="text-amber-500 font-headline tracking-wider">BIO</Label>
                      <Textarea
                        id="bio"
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        placeholder="Tell us about your investigative background..."
                        rows={4}
                        className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                        data-testid="bio-input"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleSaveProfile}
                        disabled={savingProfile}
                        className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider"
                        data-testid="save-profile-btn"
                      >
                        {savingProfile ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : null}
                        SAVE CHANGES
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setIsEditing(false);
                          setFormData({
                            displayName: currentUser.displayName || '',
                            bio: currentUser.bio || '',
                            email: currentUser.email || ''
                          });
                        }}
                        className="border-gray-600 text-gray-400 hover:bg-gray-800 font-headline tracking-wider"
                      >
                        CANCEL
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-amber-500/70 font-headline tracking-wider text-xs">DISPLAY NAME</Label>
                      <p className="text-lg text-gray-100 font-typewriter">{currentUser.displayName}</p>
                    </div>
                    <div>
                      <Label className="text-amber-500/70 font-headline tracking-wider text-xs">BIO</Label>
                      <p className="text-base text-gray-300 font-typewriter">{currentUser.bio || 'No bio added yet'}</p>
                    </div>
                    <div>
                      <Label className="text-amber-500/70 font-headline tracking-wider text-xs">EMAIL</Label>
                      <p className="text-base text-gray-300 font-typewriter">{currentUser.email}</p>
                    </div>
                    <div>
                      <Label className="text-amber-500/70 font-headline tracking-wider text-xs">JOINED THE BUREAU</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Calendar className="w-4 h-4 text-amber-500/70" />
                        <p className="text-base text-gray-300 font-typewriter">{formatDate(currentUser.createdAt)}</p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setIsEditing(true)}
                      className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider"
                      data-testid="edit-profile-btn"
                    >
                      EDIT PROFILE
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Subscription Card */}
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">CLEARANCE LEVEL</CardTitle>
                <CardDescription className="font-typewriter text-gray-400">Your current access level and privileges</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: `${getTierColor(currentTier.name)}20` }}>
                      <Crown className="w-6 h-6" style={{ color: getTierColor(currentTier.name) }} />
                    </div>
                    <div>
                      <h3 className="font-headline text-lg text-gray-100 tracking-wide">{currentTier.name?.toUpperCase()} CLEARANCE</h3>
                      <p className="text-sm text-gray-400 font-typewriter">
                        {currentTier.price === 0 ? 'Free Access' : `$${currentTier.price}/month`}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => window.location.href = '/tiers'}
                    className="border-amber-700 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider"
                  >
                    {currentTier.price === 0 ? 'UPGRADE' : 'CHANGE LEVEL'}
                  </Button>
                </div>
                <Separator className="bg-amber-900/30" />
                <div>
                  <h4 className="font-headline text-amber-500 tracking-wider mb-3">YOUR PRIVILEGES</h4>
                  <ul className="space-y-2">
                    {(currentTier.benefits || []).map((benefit, idx) => (
                      <li key={`benefit-${idx}`} className="flex items-start gap-2 text-sm font-typewriter text-gray-300">
                        <span className="text-green-500">✓</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Membership & billing — cancel option for paid tiers */}
                {!isOwner && !isModerator && currentUser?.subscriptionTier && currentUser.subscriptionTier !== 'free' && (
                  <>
                    <Separator className="bg-amber-900/30" />
                    <div id="billing" className="border border-amber-900/40 bg-black/40 rounded-lg p-4 space-y-3 scroll-mt-24" data-testid="membership-billing-box">
                      <h4 className="font-headline text-amber-500 tracking-wider">MEMBERSHIP & BILLING</h4>
                      {currentUser.subscriptionStatus === 'canceling' ? (
                        <p className="text-sm text-gray-300 font-typewriter" data-testid="membership-canceling-note">
                          Your membership is cancelled and will not renew. You keep {currentTier.name} access until the end of the period you've paid for
                          {currentUser.subscriptionExpiresAt ? ` (${new Date(currentUser.subscriptionExpiresAt).toLocaleDateString()})` : ''}.
                        </p>
                      ) : currentUser.autoRenews ? (
                        <>
                          <p className="text-sm text-gray-300 font-typewriter">
                            Your {currentTier.name} membership renews automatically each month at ${Number(currentTier.price).toFixed(2)} USD until you cancel.
                            Cancel anytime — you keep access until the end of the period you've paid for.
                          </p>
                          <Button
                            variant="outline"
                            disabled={cancelingSubscription}
                            onClick={handleCancelSubscription}
                            className="w-full sm:w-auto border-red-700/60 text-red-400 hover:bg-red-900/20 font-headline tracking-wider"
                            data-testid="cancel-subscription-btn"
                          >
                            {cancelingSubscription ? 'CANCELLING...' : 'CANCEL MEMBERSHIP'}
                          </Button>
                        </>
                      ) : (
                        <p className="text-sm text-gray-300 font-typewriter" data-testid="membership-no-renewal-note">
                          Your {currentTier.name} membership does not renew automatically. Questions about billing? Email{' '}
                          <a href="mailto:deadlinebydaria@gmail.com" className="text-amber-500 hover:text-amber-400 underline">deadlinebydaria@gmail.com</a>.
                        </p>
                      )}
                      <p className="text-xs text-gray-500 font-typewriter" data-testid="statement-descriptor-note">
                        Charges appear on your statement as <span className="text-gray-300">DEADLINE BY DARIA</span> (PayPal payments show as a PayPal charge to Deadline by Daria).
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <NotificationSettings />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">ACCOUNT SETTINGS</CardTitle>
                <CardDescription className="font-typewriter text-gray-400">Manage your account security and preferences</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-amber-500 font-headline tracking-wider">EMAIL ADDRESS</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      value={currentUser.email} 
                      disabled 
                      className="bg-gray-800 border-amber-900/50 font-typewriter text-gray-400"
                    />
                    <p className="text-xs text-gray-500 mt-1 font-typewriter">
                      Email changes require verification
                    </p>
                  </div>

                  <Separator className="bg-amber-900/30" />

                  <div>
                    <h3 className="font-headline text-amber-500 tracking-wider mb-3">CHANGE PASSWORD</h3>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="currentPassword" className="text-gray-400 font-typewriter text-sm">Current Password</Label>
                        <Input 
                          id="currentPassword" 
                          type="password" 
                          className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                        />
                      </div>
                      <div>
                        <Label htmlFor="newPassword" className="text-gray-400 font-typewriter text-sm">New Password</Label>
                        <Input 
                          id="newPassword" 
                          type="password" 
                          className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                        />
                      </div>
                      <div>
                        <Label htmlFor="confirmPassword" className="text-gray-400 font-typewriter text-sm">Confirm New Password</Label>
                        <Input 
                          id="confirmPassword" 
                          type="password" 
                          className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                        />
                      </div>
                      <Button className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider">
                        UPDATE PASSWORD
                      </Button>
                    </div>
                  </div>

                  <Separator className="bg-amber-900/30" />

                  <div>
                    <h3 className="font-headline text-red-500 tracking-wider mb-2">DANGER ZONE</h3>
                    <p className="text-sm text-gray-500 mb-3 font-typewriter">
                      Once you delete your account, all your case files and data will be permanently erased.
                    </p>
                    {!showDeleteConfirm ? (
                      <Button
                        variant="destructive"
                        className="font-headline tracking-wider"
                        onClick={() => setShowDeleteConfirm(true)}
                        data-testid="delete-account-btn"
                      >
                        DELETE ACCOUNT
                      </Button>
                    ) : (
                      <div className="space-y-3 p-4 border border-red-500/30 rounded-md bg-red-950/20">
                        <p className="text-sm text-red-400 font-typewriter">
                          This action is irreversible. All your data, messages, and subscription will be permanently deleted.
                        </p>
                        <div className="flex gap-3">
                          <Button
                            variant="destructive"
                            className="font-headline tracking-wider"
                            onClick={handleDeleteAccount}
                            disabled={deletingAccount}
                            data-testid="confirm-delete-account-btn"
                          >
                            {deletingAccount ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> DELETING...</> : 'YES, DELETE MY ACCOUNT'}
                          </Button>
                          <Button
                            variant="outline"
                            className="font-headline tracking-wider border-amber-900/50 text-gray-400"
                            onClick={() => setShowDeleteConfirm(false)}
                            disabled={deletingAccount}
                            data-testid="cancel-delete-account-btn"
                          >
                            CANCEL
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ProfilePage;
